import { Injectable, inject, NgZone, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthStateService } from './auth-state.service';
import { CompanyConfigService } from './company-config.service';
import { ToastService } from './toast.service';
import { ApiClientService } from './api-client.service';
import { TokenService } from './token.service';
import { CryptoTokenStorageService } from './crypto-token-storage.service';
import { StateSnapshotService } from '../state-preservation/services/state-snapshot.service';
import { StorageIntegrityService } from './storage-integrity.service';
import { API_ENDPOINTS, REQUEST_CONFIGS } from '../config/api-endpoints';
import {
  LoginCredentials,
  LoginResponse,
  User,
  DynamicCompanyResponse
} from '../models/dynamic-company.models';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly apiClient = inject(ApiClientService);
  private readonly tokenService = inject(TokenService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly injector = inject(Injector);
  private readonly authStateService = inject(AuthStateService);
  private readonly companyConfigService = inject(CompanyConfigService);
  private readonly toastService = inject(ToastService);
  private readonly stateSnapshotService = inject(StateSnapshotService);
  private readonly storageIntegrityService = inject(StorageIntegrityService);
  private readonly cryptoStorage = inject(CryptoTokenStorageService);
  private searchHistoryService?: any; // Lazy-loaded to avoid circular dependency

  /** Resolves when startup auth initialization (including async token rehydration) is complete. */
  readonly whenReady: Promise<void>;
  private _resolveReady!: () => void;

  // Public computed signals from AuthStateService
  readonly user = this.authStateService.user;
  readonly isAuthenticated = this.authStateService.isAuthenticated;
  readonly isLoading = this.authStateService.isLoading;
  readonly permissions = this.authStateService.permissions;
  readonly currentCompany = this.authStateService.currentCompany;
  readonly isCustomerLogin = this.authStateService.isCustomerLogin;

  constructor() {
    this.whenReady = new Promise<void>(resolve => { this._resolveReady = resolve; });
    this.initializeAuth();
  }

  /**
   * Initialize authentication state from localStorage
   */
  private initializeAuth(): void {
    try {
      this.purgeLegacyTokenStorage();

      // Get stored companies and current company preference
      const storedCompanies = this.companyConfigService.getStoredCompanies();
      const currentCompany = localStorage.getItem('currentCompany') || 'unity';

      // Primary check: login flags
      const isLoggedIn = JSON.parse(localStorage.getItem('isLoggedIn') || 'false');
      const isCustomerLogin = JSON.parse(localStorage.getItem('isCustomerLogin') || 'false');

      if (!isLoggedIn || !isCustomerLogin) {
        // Fallback: check if actual session data exists (user details for any company).
        // The flags can get out of sync (e.g. a failed token refresh called clearAuth on
        // a previous page load) while the real session data is still intact.
        if (storedCompanies.length > 0) {
          console.log('Auth flags are false but session data exists — recovering');
          localStorage.setItem('isLoggedIn', JSON.stringify(true));
          localStorage.setItem('isCustomerLogin', JSON.stringify(true));
        } else {
          console.log('User not logged in, clearing auth state');
          this.clearAuth();
          this._resolveReady();
          return;
        }
      }

      if (storedCompanies.length === 0) {
        console.warn('No stored company data found');
        this.clearAuth();
        this._resolveReady();
        return;
      }

      // Validate current company exists in stored data
      const finalCompany = storedCompanies.includes(currentCompany)
        ? currentCompany
        : storedCompanies[0];

      const userKey = this.companyConfigService.getCompanySpecificKey('userDetails', finalCompany);

      const userStr = localStorage.getItem(userKey);
      const token = this.tokenService.getToken(finalCompany);

      if (!userStr) {
        console.warn('Missing user data, clearing auth state');
        this.clearAuth();
        this._resolveReady();
        return;
      }

      const user = JSON.parse(userStr);

      if (!token) {
        // Access token is memory-only. Try restoring from encrypted IndexedDB first,
        // then fall back to rehydrating from refresh cookie.
        this.authStateService.setAuthState({
          user,
          isAuthenticated: false,
          isLoading: true,
          currentCompany: finalCompany,
          isCustomerLogin: true,
          error: null
        });

        this.tokenService.restoreTokenFromStorage(finalCompany)
          .then(async (restored) => {
            if (restored) {
              // Successfully restored from encrypted IndexedDB – no API call needed
              this.authStateService.setAuthState({
                user,
                token: restored.token,
                isAuthenticated: true,
                isLoading: false,
                currentCompany: finalCompany,
                isCustomerLogin: true,
                error: null
              });
              return;
            }

            // IndexedDB empty or expired – fall back to refresh cookie
            return this.tokenService.refreshToken(finalCompany)
              .then(() => {
                const refreshedToken = this.tokenService.getToken(finalCompany);
                if (!refreshedToken) {
                  throw new Error('Token refresh did not return an access token');
                }

                this.authStateService.setAuthState({
                  user,
                  token: refreshedToken,
                  isAuthenticated: true,
                  isLoading: false,
                  currentCompany: finalCompany,
                  isCustomerLogin: true,
                  error: null
                });
              });
          })
          .catch(error => {
            console.warn('Startup token rehydration failed:', error);
            this.clearAuth();
          })
          .finally(() => {
            this._resolveReady();
          });

        return;
      }

      // Check if token is expired
      if (this.isTokenExpiredForCompany(finalCompany)) {
        console.warn('Stored token is expired, clearing auth state');
        this.clearAuth();
        this._resolveReady();
        return;
      }

      // Restore auth state
      this.authStateService.setAuthState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        currentCompany: finalCompany,
        isCustomerLogin: true,
        error: null
      });

      // Keep the generic 'userDetails' key up to date with whoever is the active
      // company so code that reads it always gets the current session's user.
      localStorage.setItem('userDetails', JSON.stringify(user));

      console.log('Authentication state initialized successfully for company:', finalCompany);
      this._resolveReady();

    } catch (error) {
      console.error('Error initializing auth:', error);
      this.clearAuth();
      this._resolveReady();
    }
  }

  /**
   * Login with credentials supporting dynamic multi-company authentication
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse>;
  async login(username: string, password: string): Promise<LoginResponse>;
  async login(
    usernameOrCredentials: string | LoginCredentials, 
    password?: string
  ): Promise<LoginResponse> {
    // Handle both signatures
    let loginCredentials: LoginCredentials;
    
    if (typeof usernameOrCredentials === 'string') {
      loginCredentials = {
        userName: usernameOrCredentials,
        password: password!,
        companyId: "UNITYCRM0021"
      };
    } else {
      loginCredentials = usernameOrCredentials;
    }

    this.authStateService.setLoading(true);
    this.authStateService.setError(null);
    
    try {
      const loginData = {
        userName: loginCredentials.userName,
        password: loginCredentials.password,
        groupId: '53b488d5-0b50-4c83-b0e7-309caaec48ae',
        companyId: '2e939324-2b33-47e4-a410-df219d42c3ed'
      };

      const response = await firstValueFrom(
        this.apiClient.post<LoginResponse>(
          `${environment.CAS_BASE_RUL}api/CAS/login`,
          loginData,
          { ...REQUEST_CONFIGS.auth, withCredentials: true, headers: { 'X-Client-Code': 'CRM' } }
        )
      );

      if (!response.status && !response.success) {
        const errorMessage = response.data?.message || response.error || 'Login failed. Please try again.';
        this.authStateService.setError(errorMessage);
        throw new Error(errorMessage);
      }

      // Call tokenRefresh API after successful login (temporary)
      // try {
      //   const _companies = response.data?.companies || [];
      //   const _storedCompany = localStorage.getItem('currentCompany');
      //   const _activeCompany = (_storedCompany && _companies.includes(_storedCompany))
      //     ? _storedCompany
      //     : _companies[0];
      //   const _companyData = response.data?.data?.[_activeCompany];
      //   const _companyUserId = _companyData?.data?.[0]?.userID;

      //   // if (_companyData?.refreshToken) {
      //     await firstValueFrom(
      //       this.apiClient.post<any>(
      //         `${environment.CAS_BASE_RUL}api/CAS/tokenRefresh`,
      //         {
      //           userID: _companyUserId,
      //           companyId:_activeCompany,
      //           // refreshToken: _companyData.refreshToken,
      //           userName: loginCredentials.userName,
      //         },
      //         { ...REQUEST_CONFIGS.auth, withCredentials: true }
      //       )
      //     );
      //   // }
      // } catch (refreshError) {
      //   console.error('tokenRefresh call failed after login:', refreshError);
      //   // Don't block login if tokenRefresh call fails
      // }

      // Handle change password requirement
      if (response.data?.isChangePasswordRequired) {
        this.authStateService.setLoading(false);
        return response.data;
      }

      await this.processLoginResponse(response as any);
      this.purgeLegacyTokenStorage();

      // Snapshot non-sensitive state so future integrity checks have a baseline.
      await this.storageIntegrityService.saveState();

      this.authStateService.setLoading(false);
      return response.data;
      
    } catch (error: any) {
      this.authStateService.setLoading(false);
      
      const errorMessage = error?.message || 'Login failed. Please try again.';
      this.authStateService.setError(errorMessage);
      
      if (error.success === false) {
        throw new Error(error.error || errorMessage);
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Process successful login response with dynamic company support
   */
  private async processLoginResponse(response: LoginResponse): Promise<void> {
    // Support both old format (response.companies) and new format (response.meta.companies)
    const companies = response.companies || response.meta?.companies;
    if (!response.data || !companies) {
      throw new Error('Invalid login response structure');
    }

    console.log("Processing dynamic login response:", response);
    
    // Process companies dynamically
    this.companyConfigService.processCompanyResponse(
      response.data as DynamicCompanyResponse, 
      companies
    );

    // Persist all company tokens to encrypted IndexedDB
    const allTokens = this.authStateService.getCompanyTokens();
    for (const [company, tokenData] of Object.entries(allTokens)) {
      if (tokenData?.token) {
        this.cryptoStorage.storeToken(company, tokenData.token, tokenData.expiresAt, tokenData.userId).catch(() => {});
      }
    }

    // Determine active company
    const currentCompany = localStorage.getItem('currentCompany');
    const activeCompany = this.companyConfigService.determineActiveCompany(currentCompany);
    
    const activeConfig = this.companyConfigService.getCompanyConfig(activeCompany);
    if (!activeConfig) {
      throw new Error(`No valid company data found for: ${activeCompany}`);
    }

    // Set active company and user data
    this.setActiveCompany(activeCompany, activeConfig.user, activeConfig.token);

    // Set session storage
    localStorage.setItem('isCustomerLogin', JSON.stringify(true));
    localStorage.setItem('isLoggedIn', JSON.stringify(true));

    // Update auth state for immediate UI feedback
    this.authStateService.setAuthState({
      user: activeConfig.user,
      token: activeConfig.token,
      isAuthenticated: true,
      isLoading: false,
      currentCompany: activeCompany,
      isCustomerLogin: true,
      error: null,
      lastActivity: new Date(),
      sessionExpiry: new Date(Date.now() + 30 * 60 * 1000) // 30 minutes default
    });

    // Navigate to redirect path — honour returnUrl from AuthGuard if present
    const returnUrl = new URLSearchParams(window.location.search).get('returnUrl');
    let redirectPath = activeConfig.user.redirect_path || '/dashboard';

    // returnUrl takes priority (set by AuthGuard when an unauthenticated user tried to access a protected route)
    // Sanitise to prevent open-redirect: must start with "/" and must NOT start with "//" or contain "://"
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//') && !returnUrl.includes('://')) {
      redirectPath = returnUrl;
    }

    this.router.navigateByUrl(redirectPath);

    // Start new search history session and initialize companies
    try {
      if (!this.searchHistoryService) {
        const { SearchHistoryService } = await import('./search-history.service');
        this.searchHistoryService = this.injector.get(SearchHistoryService);
      }

      // Start new session
      this.searchHistoryService.startNewSession();
      console.log('New search history session started for login');

      // Initialize companies from auth (ensure IndexedDB stores exist for all companies)
      await this.searchHistoryService.initializeCompaniesFromAuth();
      console.log('Search history companies initialized');
    } catch (error) {
      console.error('Failed to start search history session:', error);
    }

    // Show success toast
    this.ngZone.run(() => {
      this.toastService.success(
        'Login Successful',
        `Welcome back, ${activeConfig.user.name}!`
      );
    });

    console.log(`Login successful for company: ${activeCompany}`);
  }

  /**
   * Set active company configuration
   */
  private setActiveCompany(companyName: string, user: User, token: string): void {
    const config = this.companyConfigService.getCompanyConfig(companyName);
    if (!config) return;

    // Update current company
    this.authStateService.setCurrentCompany(companyName);
    
    // Update localStorage
    localStorage.setItem('currentCompany', companyName);
    localStorage.setItem('BaseUrl', config.baseUrl);
    
    // Store base user details for compatibility
    localStorage.setItem('userDetails', JSON.stringify(user));
  }

  /**
   * Logout user and clear all authentication state
   */
  async logout(redirectPath: string = '/auth/login'): Promise<void> {
    // ─── Fire signout IMMEDIATELY (synchronous token read) ──────────────────
    // Must happen before setLoading(), before any await, and before any other
    // logout path (e.g. interceptor) can race and clear localStorage.
    const signoutContext = this.resolveSignoutContext();
    this.apiClient.post(
      `${environment.CAS_BASE_RUL}${API_ENDPOINTS.auth.signout}`,
      {
        userName: signoutContext.userName,
        tokens: { [signoutContext.activeCompany]: signoutContext.token }
      }
    ).subscribe({ error: () => {} }); // fire-and-forget; logout must never block on this
    // ────────────────────────────────────────────────────────────────────────

    console.log('╔═══════════════════════════════════════════════════════╗');
    console.log('║    LOGOUT SEQUENCE STARTED (auth-new.service)        ║');
    console.log('╚═══════════════════════════════════════════════════════╝');
    console.log(`Redirect path: ${redirectPath}`);
    console.log(`Current time: ${new Date().toISOString()}`);

    try {
      this.authStateService.setLoading(true);

      // Step 1: Send all search data to backend BEFORE clearing anything
      console.log('\n[1/7] Sending search data to backend...');
      console.log('  IMPORTANT: localStorage should still be intact at this point');

      // Log localStorage state before sending
      const unityToken = this.tokenService.getToken('unity');
      const mingleToken = this.tokenService.getToken('mingle');
      console.log('  localStorage check:', {
        unityToken: unityToken ? 'EXISTS' : 'MISSING',
        mingleToken: mingleToken ? 'EXISTS' : 'MISSING'
      });

      try {
        // Lazy-load SearchHistoryService to avoid circular dependency
        if (!this.searchHistoryService) {
          const { SearchHistoryService } = await import('./search-history.service');
          this.searchHistoryService = this.injector.get(SearchHistoryService);
          console.log('  ✓ SearchHistoryService loaded');
        }

        await this.searchHistoryService.sendAllSearchDataOnLogout();
        console.log('  ✓ Search data sent to backend successfully');
      } catch (searchError) {
        console.error('  ✗ Failed to send search data to backend:', searchError);
        console.error('  Stack:', (searchError as Error).stack);
        // Don't fail logout if search data sending fails
      }

      // Step 2: Show logout notification
      console.log('\n[2/7] Showing logout notification...');
      this.toastService.info('Logged Out', 'You have been successfully logged out.');
      console.log('  ✓ Notification shown');

      // Step 3: Clear integrity snapshot FIRST to prevent concurrent requests
      // from detecting false "tampering" while localStorage is being cleared.
      console.log('\n[3/7] Clearing integrity snapshot and company configurations...');
      this.storageIntegrityService.clearState();
      this.companyConfigService.clearAll();
      console.log('  ✓ Company configurations cleared');

      // Step 4: Clear centralized state management
      console.log('\n[4/7] Clearing centralized state management...');
      try {
        this.stateSnapshotService.clearAllStates();
        console.log('  ✓ All preserved component states cleared');
      } catch (stateError) {
        console.error('  ✗ Failed to clear state management:', stateError);
        // Don't fail logout if state clearing fails
      }

      // Step 5: Reset auth state
      console.log('\n[5/7] Resetting auth state...');
      this.authStateService.reset();
      console.log('  ✓ Auth state reset');

      // Step 5.3: Clear route permissions from IndexedDB so the next user never
      // sees stale menu permissions from this session (root cause of the
      // infinite-redirect loop in MenuPermissionGuard on low-end devices).
      console.log('\n[5.3/7] Clearing route permissions cache...');
      try {
        const { PermissionService } = await import('@shared/permission-service.ts/resorces-permission.service');
        const permissionService = this.injector.get(PermissionService);
        await permissionService.clearAllPermissions();
        console.log('  ✓ Route permissions cleared from memory and IndexedDB');
      } catch (permError) {
        console.error('  ✗ Failed to clear route permissions (non-fatal):', permError);
      }

      // Step 5.5: Clear encrypted token storage
      console.log('\n[5.5/7] Clearing encrypted token storage...');
      await this.cryptoStorage.clearAll().catch(() => {});
      console.log('  ✓ Encrypted tokens cleared');

      // Step 6: Clear ALL localStorage items completely
      console.log('\n[6/7] Clearing localStorage...');
      console.log('  WARNING: All tokens and user data will be deleted');
      localStorage.clear();
      console.log('  ✓ localStorage cleared');

      // Step 7: Navigate to login page
      console.log('\n[7/7] Navigating to login page...');
      this.ngZone.run(() => {
        this.router.navigate([redirectPath]);
      });
      console.log('  ✓ Navigation initiated');

      console.log('\n╔═══════════════════════════════════════════════════════╗');
      console.log('║    LOGOUT SEQUENCE COMPLETED (auth-new.service)       ║');
      console.log('╚═══════════════════════════════════════════════════════╝');

    } catch (error) {
      console.error('\n╔═══════════════════════════════════════════════════════╗');
      console.error('║    LOGOUT SEQUENCE FAILED (auth-new.service)          ║');
      console.error('╚═══════════════════════════════════════════════════════╝');
      console.error('Error during logout:', error);
      console.error('Stack:', (error as Error).stack);

      // Still navigate to login even if there was an error
      console.log('Attempting emergency navigation to login page...');
      this.ngZone.run(() => {
        this.router.navigate([redirectPath]);
      });
    } finally {
      this.authStateService.setLoading(false);
    }
  }

  /**
   * Change user password
   */
  async changePassword(data: { newPassword: string; currentPassword?: string }): Promise<boolean> {
    try {
      const payload = {
        company_group_id: 1,
        user_name: this.getUserName(),
        new_password: data.newPassword,
        ...(data.currentPassword && { current_password: data.currentPassword })
      };

      const response = await firstValueFrom(
        this.apiClient.post<any>(
          API_ENDPOINTS.auth.changePassword,
          payload,
          { 
            ...REQUEST_CONFIGS.auth,
            showLoading: true 
          }
        )
      );

      if (response.success && response.data?.Status === true) {
        await this.logout();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error changing password:', error);
      return false;
    }
  }

  /**
   * Switch to different company
   */
  async switchCompany(companyName: string): Promise<boolean> {
    try {
      const success = this.companyConfigService.switchCompany(companyName);
      
      if (success) {
        const config = this.companyConfigService.getCompanyConfig(companyName);
        if (config) {
          let token = config.token;

          // Token may be empty after a page refresh (tokens are memory-only).
          // Set the new company's user context first so performTokenRefresh
          // picks up the correct userID, then rehydrate the token.
          if (!token) {
            this.authStateService.setAuthState({
              user: config.user,
              currentCompany: companyName
            });
            try {
              await this.tokenService.refreshToken(companyName);
              token = this.tokenService.getToken(companyName) || '';
            } catch (refreshError) {
              console.warn('[AuthService] Token refresh during company switch failed:', refreshError);
            }
          }

          // Update auth state
          this.authStateService.setAuthState({
            user: config.user,
            token,
            isAuthenticated: !!token,
            currentCompany: companyName,
            lastActivity: new Date()
          });

          // Keep the generic 'userDetails' key in sync with the active company so
          // any code that still reads from it gets the correct user for this session.
          localStorage.setItem('userDetails', JSON.stringify(config.user));

          this.toastService.success(
            'Company Switched',
            `Switched to ${config.displayName}`
          );

          // Full page reload so all components and services reinitialise
          // with the new company's context (token, user, base URL, etc.)
          const redirectPath = config.user.redirect_path || '/dashboard';
          setTimeout(() => {
            window.location.href = redirectPath;
          }, 500);

          return true;
        }
      }
      
      this.toastService.error('Switch Failed', `Could not switch to ${companyName}`);
      return false;
      
    } catch (error) {
      console.error('Error switching company:', error);
      this.toastService.error('Switch Failed', 'An error occurred while switching companies');
      return false;
    }
  }

  /**
   * Utility methods
   */
  getUserId(): number {
    return this.user()?.userID || 0;
  }

  getUserName(): string {
    return this.user()?.userName || '';
  }

  getCurrentCompany(): string {
    return this.currentCompany() || localStorage.getItem('currentCompany') || 'unity';
  }

  /** Alias for getCurrentCompany (backward compatibility) */
  getCompanyName(): string {
    return this.getCurrentCompany();
  }

  /** Get buyflow host URL for a company from environment config */
  getBuyFlowHost(companyName: string): any {
    return (environment as any)[`${companyName}_BUYFLOW`] || null;
  }

  isUserLoggedIn(): boolean {
    return this.isAuthenticated() || JSON.parse(localStorage.getItem('isLoggedIn') || 'false');
  }

  hasPermission(permission: string): boolean {
    return this.authStateService.hasPermission(permission);
  }

  hasRole(role: string): boolean {
    return this.authStateService.hasRole(role);
  }

  /**
   * Check if token is expired for specific company
   */
  isTokenExpiredForCompany(companyName: string): boolean {
    return this.tokenService.validateToken(companyName).isExpired;
  }

  /**
   * Get available companies
   */
  getAvailableCompanies(): string[] {
    return this.companyConfigService.getAvailableCompanies();
  }

  /**
   * Check if multiple companies are available
   */
  hasMultipleCompanies(): boolean {
    return this.companyConfigService.hasMultipleCompanies();
  }

  /**
   * Clear authentication state
   */
  private clearAuth(): void {
    this.authStateService.reset();
    localStorage.setItem('isLoggedIn', JSON.stringify(false));
    localStorage.setItem('isCustomerLogin', JSON.stringify(false));
  }

  private purgeLegacyTokenStorage(): void {
    const keys = Object.keys(localStorage);
    const sensitiveSuffixes = ['-Token', '-TokenExpiry', '-Refreshtoken', '-RefreshTokenExpiry'];

    keys.forEach(key => {
      if (key.startsWith('tibss.') && sensitiveSuffixes.some(suffix => key.endsWith(suffix))) {
        localStorage.removeItem(key);
      }
    });
  }

  private resolveSignoutContext(): { activeCompany: string; userName: string; token: string } {
    const companyTokens = this.authStateService.getCompanyTokens();
    const activeCompany = this.authStateService.currentCompany()
      || localStorage.getItem('currentCompany')
      || Object.keys(companyTokens)[0]
      || 'unity';

    const fallbackToken = Object.values(companyTokens)
      .map(tokenData => tokenData?.token)
      .find((token): token is string => !!token) || '';

    const token = this.tokenService.getToken(activeCompany)
      || this.tokenService.getCurrentToken()
      || this.authStateService.token()
      || this.companyConfigService.getCompanyConfig(activeCompany)?.token
      || fallbackToken
      || '';

    const userName = this.authStateService.getUserName()
      || this.readStoredUserName(activeCompany)
      || '';

    return {
      activeCompany,
      userName,
      token
    };
  }

  private readStoredUserName(companyName: string): string {
    const companyKey = companyName ? `tibss.${companyName}-userDetails` : '';
    const rawCompanyUser = companyKey ? localStorage.getItem(companyKey) : null;
    const rawGlobalUser = localStorage.getItem('userDetails');

    const fromCompany = this.extractUserName(rawCompanyUser);
    if (fromCompany) {
      return fromCompany;
    }

    return this.extractUserName(rawGlobalUser);
  }

  private extractUserName(rawUser: string | null): string {
    if (!rawUser) {
      return '';
    }

    try {
      const parsed = JSON.parse(rawUser) as { userName?: string; name?: string };
      return parsed.userName || parsed.name || '';
    } catch {
      return '';
    }
  }
}
